import {defineMessages, useIntl} from 'react-intl'
import {useSelector} from 'react-redux'
import {useNightlyForAllRooms} from 'components/Accommodation/hooks/useNightlyForAllRooms'
import useShowOverlay from 'hooks/useShowOverlay/useShowOverlay'
import {
  getParametrizedHotelContext,
  getSearchContext
} from 'middleware/analytics/selectors'
import {
  getCurrencyCode,
  getDeviceType,
  getLocaleCode,
  getLocalTaxesIncluded,
  getTaxesIncluded,
  getUserCountryCode
} from 'modules/meta/selectors'
import {OverlayType} from 'modules/overlay/types'
import {
  getNumberOfGuests,
  getNumberOfRooms,
  getOfferEntity,
  getShowTotalPrices
} from 'modules/sapiSearch/selectors'
import {getSearchBoxValues} from 'modules/searchBox/selectors'
import {
  getCheapestOfferTotalRate,
  getCheapestTopOfferByNightlyRate
} from 'utils/offers'

import {
  getWhiteLabelUserId,
  selectIsAuthenticated
} from '@vio/core/src/auth/modules/selectors'
import {PriceWatchBell} from '@vio/shared/src/priceWatch/components/PriceWatchBell'
import {usePriceWatchToggleLogic} from '@vio/shared/src/priceWatch/hooks/usePriceWatchToggleLogic'
import {
  PriceWatchCTAParams,
  PriceWatchPayload
} from '@vio/shared/src/priceWatch/types'

import {ALERTS_ROUTE} from '../../constants'
import {PRICE_WATCH_CTA_COMPONENT_NAME} from './constants'

import type {ContentHotel, Hotel} from '@findhotel/sapi'

/**
 * The price-basis labels the AP and the Price Watch overlay already show next to a
 * price. Reused so the prompt names the basis in every language we ship.
 */
const messages = defineMessages({
  perNight: {id: 'perNight', defaultMessage: '/ night'},
  total: {id: 'total', defaultMessage: 'total'}
})

interface UsePriceAlertPromptCtaResult {
  /** Localized current price for the body, with its basis (e.g. "€394 / night"). */
  priceLabel: string
  /**
   * The Price Watch click handler. Signed-in → enables immediately + shows the
   * existing confirmation toast; signed-out → opens the existing SUSI overlay
   * and enables after auth. Reuses `usePriceWatchToggleLogic` unchanged.
   */
  handleClick: (event: React.SyntheticEvent<Element, Event>) => void
}

/**
 * Assembles the Price Watch params for the proactive AP price-alert prompt and
 * returns the reusable `handleClick` action plus the display price.
 *
 * Mirrors the data assembly in `components/PriceWatchCTAWrapper/PriceWatchCTAWrapper`
 * (kept local so the prompt never alters the shared CTA). We call
 * `usePriceWatchToggleLogic` directly because we render our own button, not the bell.
 */
export const usePriceAlertPromptCta = (
  hotel: Hotel | ContentHotel | undefined
): UsePriceAlertPromptCtaResult => {
  const intl = useIntl()
  const currencyCode = useSelector(getCurrencyCode)
  const locale = useSelector(getLocaleCode)
  const localTaxesIncluded = useSelector(getLocalTaxesIncluded)
  const taxesIncluded = useSelector(getTaxesIncluded)
  const isAuthenticated = useSelector(selectIsAuthenticated)
  const whiteLabelUserId = useSelector(getWhiteLabelUserId)
  const hotelContext = getParametrizedHotelContext(hotel?.objectID)
  const {checkIn, checkOut, rooms} = useSelector(getSearchBoxValues)
  const userCountry = useSelector(getUserCountryCode)
  const deviceType = useSelector(getDeviceType)
  const roomsNumber = useSelector(state =>
    getNumberOfRooms(state, hotel?.objectID)
  )
  const guests = useSelector(getNumberOfGuests)
  const offerEntity = useSelector(state =>
    getOfferEntity(state, hotel?.objectID)
  )
  const shouldShowTotal = useSelector(getShowTotalPrices)
  const nightlyForAllRooms = useNightlyForAllRooms(hotel?.objectID ?? '')
  const showOverlay = useShowOverlay()

  const cheapestOffer = getCheapestTopOfferByNightlyRate(offerEntity?.offers)
  const cheapestTotalRate = getCheapestOfferTotalRate(offerEntity?.offers)

  const rate = {
    taxesIncluded,
    localTaxesIncluded,
    base: cheapestOffer?.rate.base,
    hotelFees: cheapestOffer?.rate.hotelFees,
    taxes: cheapestOffer?.rate.taxes
  } satisfies PriceWatchPayload['rate']

  // Opens the existing PriceWatch SUSI overlay for signed-out users (bottom sheet
  // on mobile / popup on desktop). Our prompt auto-closes when this overlay opens.
  const setWebBottomSheetOpen = () => {
    if (!hotel) return
    showOverlay({
      type: OverlayType.PriceWatchOverlay,
      params: {
        hotel,
        rate: {
          baseRate: rate.base,
          localTaxes: rate.hotelFees,
          taxes: rate.taxes,
          nightlyRate: cheapestOffer?.nightlyRate,
          totalRate: cheapestTotalRate,
          localTaxesIncluded,
          taxesIncluded,
          shouldShowTotal,
          currency: cheapestOffer?.currency
        },
        flow: PRICE_WATCH_CTA_COMPONENT_NAME
      }
    })
  }

  const {handleClick} = usePriceWatchToggleLogic({
    checkIn,
    checkOut,
    rooms,
    roomsNumber,
    currency: currencyCode,
    locale,
    hotel: hotel as Hotel | ContentHotel,
    rate,
    getSearchContext,
    hotelContext,
    isAuthenticated: Boolean(isAuthenticated || whiteLabelUserId),
    roomName: cheapestOffer?.roomName,
    // Unused by the toggle logic (only PriceWatchCTA renders it); required by the type.
    ctaComponent: PriceWatchBell,
    componentName: PRICE_WATCH_CTA_COMPONENT_NAME,
    guests,
    setWebBottomSheetOpen,
    userCountry,
    device: deviceType,
    alertsManagementUrl: ALERTS_ROUTE
  } satisfies PriceWatchCTAParams)

  // Both figures come from the offer the alert is created from, so the quoted price
  // is one the user can find on the page. Falls back to nightly when it has no total.
  const showTotal = shouldShowTotal && cheapestOffer?.totalRate != null
  const nightlyRate =
    cheapestOffer?.nightlyRate != null
      ? nightlyForAllRooms(cheapestOffer.nightlyRate)
      : null
  const displayedRate = showTotal ? cheapestOffer.totalRate : nightlyRate

  const priceLabel =
    displayedRate != null
      ? `${intl.formatNumber(displayedRate, {
          style: 'currency',
          currency: currencyCode,
          maximumFractionDigits: 0
        })} ${intl.formatMessage(showTotal ? messages.total : messages.perNight)}`
      : ''

  return {
    priceLabel,
    handleClick
  }
}
